import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import emailService from '../services/emailService';
import './EmailImport.css';

const EmailImport = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('single');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // États pour l'upload simple
  const [singleFile, setSingleFile] = useState(null);

  // États pour l'upload ZIP
  const [zipFile, setZipFile] = useState(null);
  // États pour l'upload ZIP WhatsApp
  const [waZipFile, setWaZipFile] = useState(null);
  const [waJobStatus, setWaJobStatus] = useState(null);

  // États pour l'upload multiple
  const [multipleFiles, setMultipleFiles] = useState([]);

  // Polling pour le statut des jobs
  useEffect(() => {
    let interval;
    if (jobStatus && jobStatus.status === 'processing') {
      interval = setInterval(async () => {
        try {
          const status = await emailService.getJobStatus(jobStatus.job_id);
          setJobStatus(status);

          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(interval);
            setIsUploading(false);

            // Mettre à jour le résultat avec des informations sécurisées
            if (status.status === 'completed') {
              setUploadResult({
                type: 'success',
                message: `Import terminé ! ${status.imported || 'Plusieurs'} emails importés`,
                details: status.total_processed ? `${status.imported || 0} emails importés sur ${status.total_processed} traités` : 'Import terminé avec succès'
              });
            } else if (status.status === 'failed') {
              setUploadResult({
                type: 'error',
                message: 'Erreur lors du traitement',
                details: typeof status.error === 'string' ? status.error : 'Une erreur est survenue pendant le traitement'
              });
            }
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du statut:', error);
          clearInterval(interval);
          setIsUploading(false);
        }
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [jobStatus]);

  // Polling statut WhatsApp
  useEffect(() => {
    let interval;
    if (waJobStatus && waJobStatus.status === 'processing') {
      interval = setInterval(async () => {
        try {
          const status = await emailService.getWhatsAppJobStatus(waJobStatus.job_id);
          setWaJobStatus(status);
          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(interval);
            setIsUploading(false);
            if (status.status === 'completed') {
              setUploadResult({
                type: 'success',
                message: 'Import WhatsApp terminé',
                details: `${status.imported || 0} messages traités`,
              });
            } else {
              setUploadResult({ type: 'error', message: 'Erreur import WhatsApp', details: String(status.error || 'Erreur') });
            }
          }
        } catch (e) {
          clearInterval(interval);
          setIsUploading(false);
        }
      }, 2000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [waJobStatus]);

  const handleSingleFileUpload = async () => {
    if (!singleFile) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      const result = await emailService.uploadEmail(singleFile);
      setUploadResult({
        type: 'success',
        message: result.message || 'Email importé avec succès',
        details: result.file_id ? `Email "${result.file_id}" importé avec succès` : 'Import terminé'
      });
      setSingleFile(null);
    } catch (error) {
      setUploadResult({
        type: 'error',
        message: 'Erreur lors de l\'import',
        details: error.message
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleZipFileUpload = async () => {
    if (!zipFile) return;

    setIsUploading(true);
    setUploadResult(null);
    setJobStatus(null);

    try {
      const result = await emailService.uploadZipEmails(zipFile, (progress) => {
        setUploadProgress(progress);
      });
      setJobStatus(result);
      setUploadResult({
        type: 'info',
        message: 'Import ZIP lancé',
        details: `Job ${result.job_id} en cours de traitement...`
      });
      setZipFile(null);
    } catch (error) {
      setUploadResult({
        type: 'error',
        message: 'Erreur lors de l\'import ZIP',
        details: error.message
      });
      setIsUploading(false);
    }
  };

  const handleMultipleFilesUpload = async () => {
    if (multipleFiles.length === 0) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      const result = await emailService.uploadMultipleEmails(multipleFiles, (progress) => {
        setUploadProgress(progress);
      });
      setUploadResult({
        type: 'success',
        message: `${result.imported || 0} emails importés avec succès`,
        details: result.errors && result.errors.length > 0 ? `Erreurs: ${result.errors.join(', ')}` : 'Tous les fichiers ont été traités'
      });
      setMultipleFiles([]);
    } catch (error) {
      setUploadResult({
        type: 'error',
        message: 'Erreur lors de l\'import multiple',
        details: error.message
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getProgressColor = (progress) => {
    if (progress < 30) return '#ff4757';
    if (progress < 70) return '#ffa502';
    return '#2ed573';
  };

  return (
    <div className="email-import-container">
      <div className="import-header">
        <h1>📧 Import d'emails</h1>
        <p>Importez vos emails dans la plateforme Discover</p>
        {user && (
          <div className="user-info">
            <span className={`role-badge ${user.role?.toLowerCase() || 'user'}`}>
              {user.role === 'admin' ? '👑 Administrateur' : '👤 Utilisateur'}
            </span>
            <span className="user-name">{String(user.full_name || user.email || 'Utilisateur')}</span>
          </div>
        )}
      </div>

      <div className="import-tabs">
        <button
          className={`tab-button ${activeTab === 'single' ? 'active' : ''}`}
          onClick={() => setActiveTab('single')}
        >
          📄 Fichier unique
        </button>
        <button
          className={`tab-button ${activeTab === 'zip' ? 'active' : ''}`}
          onClick={() => setActiveTab('zip')}
        >
          📦 Archive ZIP
        </button>
        <button
          className={`tab-button ${activeTab === 'whatsapp' ? 'active' : ''}`}
          onClick={() => setActiveTab('whatsapp')}
        >
          💬 WhatsApp ZIP
        </button>
        <button
          className={`tab-button ${activeTab === 'multiple' ? 'active' : ''}`}
          onClick={() => setActiveTab('multiple')}
        >
          📁 Fichiers multiples
        </button>
      </div>

      <div className="import-content">
        {/* Upload simple */}
        {activeTab === 'single' && (
          <div className="upload-section">
            <h3>Import d'un fichier email unique</h3>
            <p>Formats supportés: .md, .eml, .msg, .txt</p>

            <div className="file-input-container">
              <input
                type="file"
                id="single-file"
                accept=".md,.eml,.msg,.txt"
                onChange={(e) => setSingleFile(e.target.files[0])}
                disabled={isUploading}
              />
              <label htmlFor="single-file" className="file-input-label">
                {singleFile ? String(singleFile.name) : 'Choisir un fichier'}
              </label>
            </div>

            {singleFile && (
              <div className="file-info">
                <p><strong>Fichier:</strong> {String(singleFile.name)}</p>
                <p><strong>Taille:</strong> {formatFileSize(singleFile.size)}</p>
                <p><strong>Type:</strong> {String(singleFile.type || 'Non détecté')}</p>
              </div>
            )}

            <button
              className="upload-button"
              onClick={handleSingleFileUpload}
              disabled={!singleFile || isUploading}
            >
              {isUploading ? '⏳ Import en cours...' : '🚀 Importer'}
            </button>

            {/* Progress d'upload pour fichier unique */}
            {isUploading && activeTab === 'single' && uploadProgress !== null && (
              <div className="job-status">
                <h4>Upload en cours</h4>
                <div className="status-info">
                  <p>Envoi du fichier vers le serveur...</p>
                </div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: '#667eea'
                      }}
                    ></div>
                  </div>
                  <span className="progress-text">{String(uploadProgress)}%</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Upload ZIP */}
        {activeTab === 'zip' && (
          <div className="upload-section">
            <h3>Import d'une archive ZIP</h3>
            <p>Archive contenant plusieurs fichiers emails (.md, .eml, .msg, .txt)</p>

            <div className="file-input-container">
              <input
                type="file"
                id="zip-file"
                accept=".zip"
                onChange={(e) => setZipFile(e.target.files[0])}
                disabled={isUploading}
              />
              <label htmlFor="zip-file" className="file-input-label">
                {zipFile ? String(zipFile.name) : 'Choisir une archive ZIP'}
              </label>
            </div>

            {zipFile && (
              <div className="file-info">
                <p><strong>Archive:</strong> {String(zipFile.name)}</p>
                <p><strong>Taille:</strong> {formatFileSize(zipFile.size)}</p>
              </div>
            )}

            <button
              className="upload-button"
              onClick={handleZipFileUpload}
              disabled={!zipFile || isUploading}
            >
              {isUploading ? '⏳ Import en cours...' : '🚀 Importer l\'archive'}
            </button>

            {/* Progress d'upload */}
            {isUploading && uploadProgress !== null && (
              <div className="job-status">
                <h4>Upload en cours</h4>
                <div className="status-info">
                  <p>Envoi du fichier vers le serveur...</p>
                </div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: '#667eea'
                      }}
                    ></div>
                  </div>
                  <span className="progress-text">{String(uploadProgress)}%</span>
                </div>
              </div>
            )}

            {/* Statut du job */}
            {jobStatus && (
              <div className="job-status">
                <h4>Statut de l'import</h4>
                <div className="status-info">
                  <p><strong>Job ID:</strong> {String(jobStatus.job_id || 'N/A')}</p>
                  <p><strong>Statut:</strong> {String(jobStatus.status || 'N/A')}</p>
                  <p><strong>Étape:</strong> {String(jobStatus.stage || 'N/A')}</p>
                  {jobStatus.message && <p><strong>Message:</strong> {String(jobStatus.message)}</p>}
                </div>

                {jobStatus.progress !== undefined && jobStatus.progress !== null && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${jobStatus.progress}%`,
                          backgroundColor: getProgressColor(jobStatus.progress)
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">{String(jobStatus.progress)}%</span>
                  </div>
                )}

                {jobStatus.imported !== undefined && jobStatus.imported !== null && (
                  <p><strong>Emails importés:</strong> {String(jobStatus.imported)} / {String(jobStatus.total_processed || '?')}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload WhatsApp ZIP */}
        {activeTab === 'whatsapp' && (
          <div className="upload-section">
            <h3>Import d'une archive WhatsApp (.zip)</h3>
            <p>Archive contenant des fichiers .txt (export WhatsApp)</p>
            <div className="file-input-container">
              <input
                type="file"
                id="wa-zip-file"
                accept=".zip"
                onChange={(e) => setWaZipFile(e.target.files[0])}
                disabled={isUploading}
              />
              <label htmlFor="wa-zip-file" className="file-input-label">
                {waZipFile ? String(waZipFile.name) : 'Choisir une archive ZIP'}
              </label>
            </div>
            {waZipFile && (
              <div className="file-info">
                <p><strong>Archive:</strong> {String(waZipFile.name)}</p>
                <p><strong>Taille:</strong> {formatFileSize(waZipFile.size)}</p>
              </div>
            )}
            <button
              className="upload-button"
              onClick={async () => {
                if (!waZipFile) return;
                setIsUploading(true);
                setUploadResult(null);
                setWaJobStatus(null);
                try {
                  const res = await emailService.uploadWhatsAppZip(waZipFile, (p) => setUploadProgress(p));
                  setWaJobStatus(res);
                  setUploadResult({ type: 'info', message: 'Import WhatsApp lancé', details: `Job ${res.job_id}` });
                  setWaZipFile(null);
                } catch (e) {
                  setUploadResult({ type: 'error', message: 'Erreur lors de l\'import WhatsApp', details: e.message });
                  setIsUploading(false);
                }
              }}
              disabled={!waZipFile || isUploading}
            >
              {isUploading ? '⏳ Import en cours...' : '🚀 Importer l\'archive WhatsApp'}
            </button>

            {/* Progress d'upload WhatsApp */}
            {isUploading && activeTab === 'whatsapp' && uploadProgress !== null && (
              <div className="job-status">
                <h4>Upload en cours</h4>
                <div className="status-info">
                  <p>Envoi du fichier vers le serveur...</p>
                </div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: '#667eea'
                      }}
                    ></div>
                  </div>
                  <span className="progress-text">{String(uploadProgress)}%</span>
                </div>
              </div>
            )}

            {/* Statut du job WhatsApp */}
            {waJobStatus && (
              <div className="job-status">
                <h4>Statut de l'import WhatsApp</h4>
                <div className="status-info">
                  <p><strong>Job ID:</strong> {String(waJobStatus.job_id || 'N/A')}</p>
                  <p><strong>Statut:</strong> {String(waJobStatus.status || 'N/A')}</p>
                  <p><strong>Étape:</strong> {String(waJobStatus.stage || 'N/A')}</p>
                  {waJobStatus.message && <p><strong>Message:</strong> {String(waJobStatus.message)}</p>}
                </div>

                {waJobStatus.progress !== undefined && waJobStatus.progress !== null && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${waJobStatus.progress}%`,
                          backgroundColor: getProgressColor(waJobStatus.progress)
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">{String(waJobStatus.progress)}%</span>
                  </div>
                )}

                {waJobStatus.imported !== undefined && waJobStatus.imported !== null && (
                  <p><strong>Messages importés:</strong> {String(waJobStatus.imported)} / {String(waJobStatus.total_processed || '?')}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload multiple */}
        {activeTab === 'multiple' && (
          <div className="upload-section">
            <h3>Import de fichiers multiples</h3>
            <p>Sélectionnez plusieurs fichiers emails à importer en une fois</p>

            <div className="file-input-container">
              <input
                type="file"
                id="multiple-files"
                accept=".md,.eml,.msg,.txt"
                multiple
                onChange={(e) => setMultipleFiles(Array.from(e.target.files))}
                disabled={isUploading}
              />
              <label htmlFor="multiple-files" className="file-input-label">
                {multipleFiles.length > 0 ? `${multipleFiles.length} fichiers sélectionnés` : 'Choisir des fichiers'}
              </label>
            </div>

            {multipleFiles.length > 0 && (
              <div className="files-list">
                <h4>Fichiers sélectionnés:</h4>
                {multipleFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">{String(file.name)}</span>
                    <span className="file-size">{formatFileSize(file.size)}</span>
                  </div>
                ))}
                <p className="total-size">
                  <strong>Taille totale:</strong> {formatFileSize(multipleFiles.reduce((total, file) => total + file.size, 0))}
                </p>
              </div>
            )}

            <button
              className="upload-button"
              onClick={handleMultipleFilesUpload}
              disabled={multipleFiles.length === 0 || isUploading}
            >
              {isUploading ? '⏳ Import en cours...' : `🚀 Importer ${multipleFiles.length} fichiers`}
            </button>

            {/* Progress d'upload pour fichiers multiples */}
            {isUploading && activeTab === 'multiple' && uploadProgress !== null && (
              <div className="job-status">
                <h4>Upload en cours</h4>
                <div className="status-info">
                  <p>Envoi des fichiers vers le serveur...</p>
                </div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: '#667eea'
                      }}
                    ></div>
                  </div>
                  <span className="progress-text">{String(uploadProgress)}%</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Résultat de l'upload */}
        {uploadResult && (
          <div className={`upload-result ${uploadResult.type}`}>
            <h4>{String(uploadResult.message || 'Résultat')}</h4>
            <p>{String(uploadResult.details || '')}</p>
          </div>
        )}
      </div>

      <div className="import-help">
        <h3>💡 Aide</h3>
        <div className="help-section">
          <h4>Formats supportés:</h4>
          <ul>
            <li><strong>.md</strong> - Fichiers Markdown avec métadonnées d'email</li>
            <li><strong>.eml</strong> - Fichiers email standard (RFC 822)</li>
            <li><strong>.msg</strong> - Fichiers Outlook</li>
            <li><strong>.txt</strong> - Fichiers texte brut ou exports WhatsApp</li>
          </ul>
        </div>

        <div className="help-section">
          <h4>WhatsApp:</h4>
          <ul>
            <li><strong>Archive ZIP</strong> - Contenant des fichiers .txt d'export WhatsApp</li>
            <li><strong>Format attendu</strong> - [DD/MM/YY HH:MM:SS] Expéditeur: Message</li>
            <li><strong>Export WhatsApp</strong> - Utiliser "Exporter la discussion" depuis l'app</li>
          </ul>
        </div>

        <div className="help-section">
          <h4>Limites:</h4>
          <ul>
            <li>Fichier unique: 10 MB maximum</li>
            <li>Archive ZIP (emails): 100 MB maximum</li>
            <li>Archive ZIP (WhatsApp): 100 MB maximum</li>
            <li>Fichiers multiples: 50 fichiers maximum</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmailImport;